import React, { useState, useEffect, useCallback } from 'react';
import { gatePassService, productService, warehouseService, customerService, batchService } from '../../services/financeService';
import { buildGatePassRows } from '../../utils/exportUtils';
import { API_BASE } from '../../utils/apiUtils';
import AttachmentPanel from './AttachmentPanel';
import { ProductForm } from './ProductManagement';
import SearchableSelect from './SearchableSelect';
import CsvActions from '../common/CsvActions';
import PdfActions from '../common/PdfActions';
import './Financial.css';

const GATE_PASS_TYPES = ['ENTRY', 'EXIT'];
const MATERIAL_TYPES = ['RAW_MATERIAL', 'PACKAGING', 'READY_CONTAINER', 'WASTE', 'OTHER'];
const PARTY_TYPES = ['SUPPLIER', 'CUSTOMER', 'TRANSPORTER', 'CONTRACTOR', 'OTHER'];
const UNITS = ['KG', 'TON', 'BAG', 'CONTAINER', 'PIECE', 'LITER', 'METER', 'BOX'];

const STATUS_STYLE = {
  DRAFT:      { background: 'var(--color-bg-tertiary)', color: 'var(--color-neutral-700)' },
  ISSUED:     { background: 'var(--color-info-light)', color: 'var(--color-info)' },
  RECEIVED:   { background: 'var(--color-success-light)', color: 'var(--color-success)' },
  DISPATCHED: { background: 'var(--color-warning-light)', color: 'var(--color-warning)' },
  CANCELLED:  { background: 'var(--color-error-light)', color: 'var(--color-error)' },
};

const TYPE_STYLE = {
  ENTRY: { background: 'var(--color-success-light)', color: 'var(--color-success)' },
  EXIT:  { background: 'var(--color-error-light)', color: 'var(--color-error)' },
};

const badge = (text, styleMap) => {
  const s = styleMap[text] || { background: 'var(--color-bg-tertiary)', color: 'var(--color-neutral-700)' };
  return (
    <span style={{ ...s, padding: '2px 10px', borderRadius: '9999px', fontSize: '11px', fontWeight: 600 }}>
      {text}
    </span>
  );
};

const fmt = (str) => str ? str.replace(/_/g, ' ') : '—';

const CURRENCIES = ['PKR', 'USD', 'EUR', 'AED', 'GBP'];

const emptyItem = () => ({ itemName: '', itemCode: '', productId: null, quantity: '', unit: 'KG', weight: '', packSizeKg: null, purchasePrice: '', priceUnit: 'KG', priceRaw: '', purchaseCurrency: 'PKR', vendorPo: '', notes: '', bagTypeProductId: '' });

const parsePackSizeKg = (packSize) => {
  if (!packSize) return null;
  const num = parseFloat(String(packSize).replace(/[^0-9.]/g, ''));
  if (isNaN(num) || num <= 0) return null;
  const lower = String(packSize).toLowerCase();
  if (lower.includes('ton')) return num * 1000;
  if (lower.includes('g') && !lower.includes('kg')) return num / 1000;
  return num; // KG or unitless — treat as KG
};
const emptyForm = () => ({
  type: 'ENTRY',
  materialType: 'RAW_MATERIAL',
  gatePassDate: new Date().toISOString().split('T')[0],
  warehouseName: '',
  batchId: '',
  _partyId: '',
  partyName: '',
  partyType: 'SUPPLIER',
  vehicleNumber: '',
  driverName: '',
  driverContact: '',
  containerNumber: '',
  notes: '',
  serviceCharges: '',
  laborCost: '',
  items: [emptyItem()],
});

const InlineAlert = ({ msg, color = 'var(--color-error)', bg = 'var(--color-error-light)' }) =>
  msg ? (
    <div style={{ background: bg, border: `1px solid ${color}`, color, borderRadius: '8px', padding: '10px 14px', marginBottom: '14px', fontSize: '13px' }}>
      {msg}
    </div>
  ) : null;

const GatePassManagement = () => {
  const [gatePasses, setGatePasses]     = useState([]);
  const [loading, setLoading]           = useState(true);
  const [pageError, setPageError]       = useState(null);
  const [activeTab, setActiveTab]       = useState('ALL');

  const [showCreate, setShowCreate]     = useState(false);
  const [showDetail, setShowDetail]     = useState(false);
  const [selectedPass, setSelectedPass] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [editingPriceItemId, setEditingPriceItemId] = useState(null);
  const [editingPriceValue, setEditingPriceValue] = useState('');
  const [savingPrice, setSavingPrice] = useState(false);

  const [form, setForm]                 = useState(emptyForm());
  const [formError, setFormError]       = useState(null);
  const [formLoading, setFormLoading]   = useState(false);

  const [actionLoading, setActionLoading] = useState(null);
  const [cancelId, setCancelId]           = useState(null);
  const [cancelReason, setCancelReason]   = useState('');
  const [dispatchConfirmId, setDispatchConfirmId] = useState(null);
  const [toast, setToast]                 = useState(null);
  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  const [search, setSearch]             = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [fromDate, setFromDate]         = useState('');
  const [toDate, setToDate]             = useState('');

  const [productCatalog, setProductCatalog] = useState([]);
  const [warehouseList, setWarehouseList] = useState([]);
  const [partyList, setPartyList] = useState([]);
  const [batchList, setBatchList] = useState([]);
  const [quickAddFor, setQuickAddFor] = useState(null); // item index awaiting a newly created product, or null

  // Labor/service placeholder products (category CONSUMABLE) are cost entries, not physical
  // stock — they must never be receivable/dispatchable via a Gate Pass.
  const purchasableProducts = productCatalog.filter(p => p.category !== 'CONSUMABLE');
  // Empty sacks (Bori/Katta) — real sellable Products the business creates once, tagged
  // BYPRODUCT, with their own packSize (e.g. "10kg Bag") used to auto-compute bag count.
  const byproductProducts = productCatalog.filter(p => p.category === 'BYPRODUCT');

  const handleProductCreated = (itemIndex) => (product) => {
    setProductCatalog(prev => [...prev, product]);
    selectProduct(itemIndex, product.id);
    setQuickAddFor(null);
  };

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setPageError(null);
    try {
      let res;
      if (fromDate && toDate)        res = await gatePassService.getByDateRange(fromDate, toDate);
      else if (activeTab !== 'ALL')  res = await gatePassService.getByType(activeTab);
      else if (statusFilter)         res = await gatePassService.getByStatus(statusFilter);
      else                           res = await gatePassService.getAll();

      if (res.success) setGatePasses(res.data?.content || res.data || []);
      else setPageError(res.message || 'Failed to load gate passes');
    } catch (e) {
      setPageError(e.message);
    } finally {
      setLoading(false);
    }
  }, [activeTab, statusFilter, fromDate, toDate]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const openCreate = () => {
    setForm(emptyForm());
    setFormError(null);
    setShowCreate(true);
    productService.getAll({ activeOnly: true }).then(res => {
      if (res.success) setProductCatalog(res.data || []);
    }).catch(() => {});
    warehouseService.getAll(true).then(res => {
      const list = res?.data || res || [];
      setWarehouseList(Array.isArray(list) ? list : []);
    }).catch(() => {});
    customerService.getAll().then(res => {
      const list = res?.data || res || [];
      setPartyList(Array.isArray(list) ? list : []);
    }).catch(() => {});
    batchService.getAll().then(res => {
      const list = res?.data || res || [];
      setBatchList(Array.isArray(list) ? list.filter(b => b.status === 'OPEN' || b.status === 'PROCESSING') : []);
    }).catch(() => {});
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setFormError(null);
    // Batch reference is mandatory for ALL gate passes
    if (!form.batchId) {
      setFormError(
        form.type === 'EXIT'
          ? 'Batch reference is required. Select the batch these goods are dispatching from.'
          : 'Batch reference is required. Create the batch first, then come back to create the gate pass.'
      );
      return;
    }
    for (const item of form.items) {
      if (!item.itemName.trim())                 { setFormError('Each item must have a name'); return; }
      if (!item.quantity || +item.quantity <= 0) { setFormError('Each item needs a valid quantity'); return; }
      if (!item.productId) {
        setFormError('Select a product from the catalog for each item — required for stock tracking'); return;
      }
    }
    setFormLoading(true);
    try {
      const payload = {
        ...form,
        batchId: form.batchId ? Number(form.batchId) : undefined,
        serviceCharges: form.serviceCharges ? parseFloat(form.serviceCharges) : undefined,
        laborCost: form.laborCost ? parseFloat(form.laborCost) : undefined,
        items: form.items.map(i => ({
          itemName: i.itemName,
          itemCode: i.itemCode || undefined,
          productId: i.productId || undefined,
          quantity: parseFloat(i.quantity),
          unit: i.unit,
          weight: i.weight ? parseFloat(i.weight) : undefined,
          purchasePrice: i.purchasePrice ? parseFloat(i.purchasePrice) : undefined,
          purchaseCurrency: i.purchaseCurrency || undefined,
          vendorPo: i.vendorPo || undefined,
          notes: i.notes || undefined,
          bagTypeProductId: i.bagTypeProductId ? Number(i.bagTypeProductId) : undefined,
        })),
      };
      const res = await gatePassService.create(payload);
      if (res.success) { setShowCreate(false); fetchAll(); }
      else setFormError(res.message || 'Failed to create gate pass');
    } catch (e) {
      setFormError(e.message);
    } finally {
      setFormLoading(false);
    }
  };

  const handleViewDetail = async (id) => {
    setShowDetail(true);
    setSelectedPass(null);
    setDetailLoading(true);
    try {
      const res = await gatePassService.getById(id);
      if (res.success) setSelectedPass(res.data);
    } catch { /* handled by null check */ }
    finally { setDetailLoading(false); }
  };

  const startEditPrice = (item) => {
    setEditingPriceItemId(item.id);
    setEditingPriceValue(item.purchasePrice || '');
  };

  const saveEditPrice = async (item) => {
    const price = parseFloat(editingPriceValue);
    if (isNaN(price) || price < 0) { showToast('Enter a valid price', 'error'); return; }
    setSavingPrice(true);
    try {
      await gatePassService.updateItemPrice(selectedPass.id, item.id, price, item.purchaseCurrency || 'PKR');
      showToast('Price updated — carried through to the Purchase Order');
      setEditingPriceItemId(null);
      const res = await gatePassService.getById(selectedPass.id);
      if (res.success) setSelectedPass(res.data);
    } catch (e) {
      showToast(e.message || 'Failed to update price', 'error');
    } finally {
      setSavingPrice(false);
    }
  };

  const handleIssue = async (id) => {
    setActionLoading(id);
    try {
      const res = await gatePassService.issue(id);
      if (res.success) fetchAll();
      else setPageError(res.message || 'Failed to issue gate pass');
    } catch (e) { setPageError(e.message); }
    finally { setActionLoading(null); }
  };

  const handleReceive = async (id) => {
    setActionLoading(id);
    try {
      const res = await gatePassService.receive(id);
      if (res.success) { fetchAll(); showToast('Gate pass received — stock updated & purchase auto-created'); }
      else setPageError(res.message || 'Failed to receive gate pass');
    } catch (e) { setPageError(e.message); }
    finally { setActionLoading(null); }
  };

  // Fetches the gate pass PDF blob — used by <PdfActions> for Preview + Download, added
  // 2026-09-14 so it can be checked before committing to a file.
  const fetchGatePassPdfBlob = async (gp) => {
    const response = await fetch(`${API_BASE}/gate-passes/${gp.id}/pdf`, {
      method: 'GET', credentials: 'include', headers: { Accept: 'application/pdf' }
    });
    if (!response.ok) throw new Error('Failed to load PDF');
    return response.blob();
  };

  const handleDispatch = (id) => setDispatchConfirmId(id);

  const doDispatch = async () => {
    const id = dispatchConfirmId;
    setDispatchConfirmId(null);
    setActionLoading(id);
    try {
      const res = await gatePassService.dispatch(id);
      if (res.success) { fetchAll(); showToast('Gate pass dispatched — stock updated'); }
      else setPageError(res.message || 'Failed to dispatch gate pass');
    } catch (e) { setPageError(e.message); }
    finally { setActionLoading(null); }
  };

  const handleCancelSubmit = async () => {
    if (!cancelReason.trim()) { setPageError('Please enter a cancellation reason'); return; }
    setActionLoading(cancelId);
    try {
      const res = await gatePassService.cancel(cancelId, cancelReason);
      if (res.success) { fetchAll(); setCancelId(null); setCancelReason(''); }
      else setPageError(res.message || 'Failed to cancel');
    } catch (e) { setPageError(e.message); }
    finally { setActionLoading(null); }
  };

  const addItem    = () => setForm(f => ({ ...f, items: [...f.items, emptyItem()] }));
  const removeItem = (i) => setForm(f => ({ ...f, items: f.items.filter((_, idx) => idx !== i) }));
  const setItem = (i, k, v) => setForm(f => ({
    ...f,
    items: f.items.map((it, idx) => {
      if (idx !== i) return it;
      const updated = { ...it, [k]: v };
      const ps = updated.packSizeKg;
      // ONE direction only: bags typed → auto-fill weight (no reverse to prevent float drift)
      if (ps && ps > 0 && k === 'quantity' && v !== '') {
        const bags = parseFloat(v);
        if (!isNaN(bags) && bags > 0) {
          updated.weight = (Math.round(bags * ps * 1000) / 1000).toFixed(3);
        }
      }
      return updated;
    })
  }));

  const setField = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const selectProduct = (i, prodId) => {
    if (!prodId) return;
    const product = productCatalog.find(p => String(p.id) === String(prodId));
    if (!product) return;
    const psKg = parsePackSizeKg(product.packSize);
    setForm(f => ({
      ...f,
      items: f.items.map((it, idx) => idx === i ? {
        ...it,
        productId: Number(product.id),
        itemName: product.name,
        itemCode: product.productCode || it.itemCode,
        unit: product.unit || it.unit,
        packSizeKg: psKg,
      } : it),
    }));
  };

  const clearFilters = () => { setFromDate(''); setToDate(''); setStatusFilter(''); setSearch(''); };
  const hasFilters = fromDate || toDate || statusFilter || search;

  const displayed = gatePasses.filter(gp =>
    !search ||
    gp.gatePassNumber?.toLowerCase().includes(search.toLowerCase()) ||
    gp.partyName?.toLowerCase().includes(search.toLowerCase()) ||
    (gp.vehicleNumber || '').toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="financial-container">

      {/* ── Header ── */}
      <div className="financial-header">
        <div>
          <h2 className="financial-title">Gate Pass Management</h2>
          <p className="financial-subtitle">Track all material entry & exit at warehouses</p>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          {(() => { const { headers, matrix } = buildGatePassRows(gatePasses); return (
            <CsvActions filename="gate_passes" headers={headers} matrix={matrix} />
          ); })()}
          <button className="btn-primary" onClick={openCreate}>+ New Gate Pass</button>
        </div>
      </div>

      {/* ── Tabs ── */}
      <div className="tab-container">
        {[
          { key: 'ALL',   label: 'All Gate Passes' },
          { key: 'ENTRY', label: 'Entry' },
          { key: 'EXIT',  label: 'Exit' },
        ].map(t => (
          <button
            key={t.key}
            className={`tab-button ${activeTab === t.key ? 'active' : ''}`}
            onClick={() => { setActiveTab(t.key); setStatusFilter(''); setFromDate(''); setToDate(''); }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* ── Filters ── */}
      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '16px', alignItems: 'center' }}>
        <input
          className="form-control"
          style={{ maxWidth: '210px' }}
          placeholder="Search by GP#, party, vehicle..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <select
          className="form-control"
          style={{ maxWidth: '160px' }}
          value={statusFilter}
          onChange={e => { setStatusFilter(e.target.value); setActiveTab('ALL'); setFromDate(''); setToDate(''); }}
        >
          <option value="">All Statuses</option>
          {['DRAFT', 'ISSUED', 'RECEIVED', 'DISPATCHED', 'CANCELLED'].map(s => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
        <input type="date" className="form-control" style={{ maxWidth: '145px' }} value={fromDate} onChange={e => setFromDate(e.target.value)} />
        <input type="date" className="form-control" style={{ maxWidth: '145px' }} value={toDate}   onChange={e => setToDate(e.target.value)} />
        {hasFilters && (
          <button className="btn-secondary" style={{ fontSize: '13px', padding: '8px 14px' }} onClick={clearFilters}>
            ✕ Clear
          </button>
        )}
      </div>

      {pageError && <InlineAlert msg={pageError} />}

      {/* ── Table ── */}
      {loading ? (
        <div className="loading-state">Loading gate passes…</div>
      ) : displayed.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">—</div>
          <h3>No gate passes found</h3>
          <p>Click "New Gate Pass" to create the first one.</p>
        </div>
      ) : (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Gate Pass #</th>
                <th>Type</th>
                <th>Material</th>
                <th>Date</th>
                <th>Party</th>
                <th>Warehouse</th>
                <th>Vehicle / Container</th>
                <th>Qty · Weight</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {displayed.map(gp => (
                <tr key={gp.id}>
                  <td>
                    <span
                      style={{ fontWeight: 600, color: 'var(--color-primary)', cursor: 'pointer' }}
                      onClick={() => handleViewDetail(gp.id)}
                      title="View details"
                    >
                      {gp.gatePassNumber}
                    </span>
                  </td>
                  <td>{badge(gp.type, TYPE_STYLE)}</td>
                  <td style={{ fontSize: '12px' }}>{fmt(gp.materialType)}</td>
                  <td style={{ fontSize: '12px', whiteSpace: 'nowrap' }}>{gp.gatePassDate}</td>
                  <td style={{ minWidth: 160 }}>
                    <div style={{ fontWeight: 500, fontSize: '13px' }}>{gp.partyName}</div>
                    <div style={{ fontSize: '11px', color: 'var(--color-text-tertiary)' }}>{gp.partyType}</div>
                  </td>
                  <td style={{ fontSize: '12px' }}>{gp.warehouseName || '—'}</td>
                  <td style={{ fontSize: '12px' }}>
                    <div>{gp.vehicleNumber || '—'}</div>
                    {gp.containerNumber && <div style={{ color: 'var(--color-accent-hover)', fontSize: '11px' }}>{gp.containerNumber}</div>}
                  </td>
                  <td style={{ fontSize: '12px', whiteSpace: 'nowrap' }}>
                    {gp.totalQuantity != null ? `${gp.totalQuantity}` : '—'}
                    {gp.totalWeight  != null ? ` · ${gp.totalWeight} kg` : ''}
                  </td>
                  <td>{badge(gp.status, STATUS_STYLE)}</td>
                  <td>
                    <div className="table-actions" style={{ gap: '5px' }}>
                      <button className="btn-icon" onClick={() => handleViewDetail(gp.id)} title="View details">View</button>

                      {gp.status === 'DRAFT' && (
                        <button
                          className="btn-icon"
                          style={{ background: 'var(--color-info-light)', color: 'var(--color-info)', border: '1px solid var(--color-info)' }}
                          disabled={actionLoading === gp.id}
                          onClick={() => handleIssue(gp.id)}
                        >
                          {actionLoading === gp.id ? 'Issuing...' : 'Issue'}
                        </button>
                      )}

                      {gp.status === 'ISSUED' && gp.type === 'ENTRY' && (
                        <button
                          className="btn-icon"
                          style={{ background: 'var(--color-success-light)', color: 'var(--color-success)', border: '1px solid var(--color-success-border)' }}
                          disabled={actionLoading === gp.id}
                          onClick={() => handleReceive(gp.id)}
                          title="Mark as received — auto-updates product stock"
                        >
                          {actionLoading === gp.id ? '...' : 'Receive'}
                        </button>
                      )}

                      {gp.status === 'ISSUED' && gp.type === 'EXIT' && (
                        <span
                          style={{ fontSize: '11px', color: 'var(--color-text-tertiary)', fontStyle: 'italic' }}
                          title="EXIT gate passes now dispatch automatically when you create an Invoice against them — see Invoices → New Invoice"
                        >
                          Via invoice
                        </span>
                      )}

                      {gp.status !== 'CANCELLED' && gp.status !== 'RECEIVED' && gp.status !== 'DISPATCHED' && (
                        <button
                          className="btn-icon"
                          style={{ background: 'var(--color-error-light)', color: 'var(--color-error)', border: '1px solid var(--color-error-border)' }}
                          onClick={() => { setCancelId(gp.id); setCancelReason(''); }}
                        >
                          Cancel
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Cancel Reason Modal ── */}
      {cancelId && (
        <div className="modal-overlay" onClick={() => setCancelId(null)}>
          <div className="modal-content" style={{ maxWidth: '420px' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Cancel Gate Pass</h3>
              <button className="modal-close" onClick={() => setCancelId(null)}>✕</button>
            </div>
            <p style={{ fontSize: '13px', color: 'var(--color-text-secondary)', marginBottom: '14px' }}>
              Please provide a reason for cancellation. This cannot be undone.
            </p>
            <div className="form-group">
              <label className="form-label">Reason *</label>
              <textarea
                className="form-control"
                rows={3}
                placeholder="e.g. Wrong vehicle, wrong material, duplicate entry..."
                value={cancelReason}
                onChange={e => setCancelReason(e.target.value)}
              />
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setCancelId(null)}>Back</button>
              <button
                className="btn-danger"
                disabled={actionLoading === cancelId}
                onClick={handleCancelSubmit}
              >
                {actionLoading === cancelId ? 'Cancelling…' : 'Confirm Cancel'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Dispatch Confirm Modal ── */}
      {dispatchConfirmId && (
        <div className="modal-overlay" onClick={() => setDispatchConfirmId(null)}>
          <div className="modal-content" style={{ maxWidth: '440px' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Confirm Dispatch</h3>
              <button className="modal-close" onClick={() => setDispatchConfirmId(null)}>✕</button>
            </div>
            <div style={{ padding: '4px 0 16px', fontSize: '14px', color: 'var(--color-neutral-700)', lineHeight: 1.6 }}>
              <p style={{ marginBottom: '10px' }}>Dispatch will <strong>deduct stock</strong> for all linked products.</p>
              <div style={{ background: 'var(--color-warning-light)', border: '1px solid var(--color-warning-border)', borderRadius: '8px', padding: '10px 14px', fontSize: '13px', color: 'var(--color-warning-strong)' }}>
                <strong>Warning:</strong> If you already raised an invoice for these goods and stock was deducted there, do <strong>not</strong> dispatch — it will double-deduct.
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setDispatchConfirmId(null)}>Go back</button>
              <button className="btn-primary" onClick={doDispatch}>Yes, dispatch</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Create Gate Pass Modal ── */}
      {showCreate && (
        <div className="modal-overlay" onClick={() => !formLoading && setShowCreate(false)}>
          <div className="modal-content" style={{ maxWidth: '780px' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>New Gate Pass</h3>
              <button className="modal-close" onClick={() => !formLoading && setShowCreate(false)}>✕</button>
            </div>

            <form onSubmit={handleCreate}>
              <InlineAlert msg={formError} />

              <div className="form-grid-2">
                <div className="form-group">
                  <label className="form-label">Type *</label>
                  <select className="form-control" value={form.type} onChange={e => {
                    setField('type', e.target.value);
                    setField('_partyId', '');
                    setField('partyName', '');
                    setField('partyType', e.target.value === 'ENTRY' ? 'SUPPLIER' : 'CUSTOMER');
                  }}>
                    {GATE_PASS_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Material Type *</label>
                  <select className="form-control" value={form.materialType} onChange={e => setField('materialType', e.target.value)}>
                    {MATERIAL_TYPES.map(t => <option key={t} value={t}>{fmt(t)}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Date *</label>
                  <input type="date" className="form-control" required value={form.gatePassDate} onChange={e => setField('gatePassDate', e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label" style={{ color: !form.batchId ? 'var(--color-error)' : 'var(--color-success)' }}>
                    Batch Reference *
                    <span style={{ fontSize: 11, fontWeight: 400, marginLeft: 8, color: 'var(--color-text-secondary)' }}>
                      {form.type === 'ENTRY'
                        ? 'Create batch first in Batch Management, then come back here'
                        : 'Select the batch being dispatched'}
                    </span>
                  </label>
                  {batchList.length === 0 ? (
                    <div style={{ padding: '7px 10px', border: '1px solid var(--color-error)', borderRadius: 6, fontSize: 13, background: 'var(--color-error-light)', color: 'var(--color-error)' }}>
                      No batches found. Go to <strong>Batch Management → + New Batch</strong> first, then return here.
                    </div>
                  ) : (
                    <SearchableSelect
                      required
                      placeholder="— Select Batch (required) —"
                      value={form.batchId}
                      options={batchList.map(b => ({
                        value: b.id,
                        label: `${b.batchCode} · ${b.productName}${b.godownName ? ` · ${b.godownName}` : ''}`,
                      }))}
                      onChange={(value) => {
                        const id = String(value);
                        setField('batchId', id);
                        if (id) {
                          const b = batchList.find(x => String(x.id) === id);
                          // Auto-fill warehouse from batch
                          if (b?.godownName && !form.warehouseName) setField('warehouseName', b.godownName);
                          // Auto-fill items from batch product — lock them so user can't change product
                          if (b?.productId) {
                            const prod = productCatalog.find(p => String(p.id) === String(b.productId));
                            setForm(f => ({
                              ...f,
                              batchId: id,
                              items: [{
                                ...emptyItem(),
                                productId: b.productId,
                                itemName: b.productName || (prod?.name || ''),
                                itemCode: prod?.productCode || '',
                                unit: prod?.unit || 'KG',
                                packSizeKg: prod?.packSize ? parseFloat(prod.packSize) : null,
                              }]
                            }));
                          }
                        } else {
                          // Batch cleared — reset items
                          setForm(f => ({ ...f, batchId: '', items: [emptyItem()] }));
                        }
                      }}
                    />
                  )}
                </div>
                <div className="form-group">
                  <label className="form-label">Warehouse</label>
                  {warehouseList.length > 0 ? (
                    <select className="form-control" value={form.warehouseName} onChange={e => setField('warehouseName', e.target.value)}>
                      <option value="">— Select Warehouse —</option>
                      {warehouseList.map(w => <option key={w.id} value={w.name}>{w.name}{w.location ? ` (${w.location})` : ''}</option>)}
                      <option value="__custom__">Other (type below)</option>
                    </select>
                  ) : (
                    <input className="form-control" placeholder="e.g. Main Warehouse, Karachi Store" value={form.warehouseName} onChange={e => setField('warehouseName', e.target.value)} />
                  )}
                  {form.warehouseName === '__custom__' && (
                    <input className="form-control" style={{ marginTop: '6px' }} placeholder="Enter warehouse name..." onChange={e => setField('warehouseName', e.target.value)} autoFocus />
                  )}
                </div>
                <div className="form-group">
                  <label className="form-label">
                    {form.type === 'ENTRY' ? 'Supplier / Vendor *' : 'Customer / Party *'}
                  </label>
                  {partyList.length > 0 ? (
                    <>
                      {(() => {
                        // One flat, searchable list beats native <select>'s three overlapping
                        // optgroups (Vendors/Customers/"All Parties" — the last one just
                        // re-listed everyone again as a safety net for mismatched customerType,
                        // so the same party could appear twice). Search makes that duplication
                        // unnecessary: sort the type that matches this gate pass direction to
                        // the top, label the rest with their type so they're still findable.
                        const primaryType = form.type === 'ENTRY' ? 'VENDOR' : 'CUSTOMER';
                        const isPrimary = (p) => p.customerType === primaryType || p.customerType === 'BOTH';
                        const sorted = [...partyList].sort((a, b) => {
                          const am = isPrimary(a), bm = isPrimary(b);
                          if (am !== bm) return am ? -1 : 1;
                          return (a.name || '').localeCompare(b.name || '');
                        });
                        const partyOptions = [
                          ...sorted.map(p => ({
                            value: p.id,
                            label: isPrimary(p) ? p.name : `${p.name} (${p.customerType})`,
                          })),
                          { value: '__other__', label: 'Type manually…' },
                        ];
                        return (
                          <SearchableSelect
                            placeholder="— Select —"
                            value={form._partyId || ''}
                            options={partyOptions}
                            onChange={(id) => {
                              if (id === '__other__') {
                                setField('_partyId', '__other__');
                                setField('partyName', '');
                              } else {
                                const p = partyList.find(x => String(x.id) === String(id));
                                if (p) {
                                  setField('_partyId', id);
                                  setField('partyName', p.name);
                                  setField('partyType',
                                    p.customerType === 'VENDOR' ? 'SUPPLIER' :
                                    p.customerType === 'CUSTOMER' ? 'CUSTOMER' : 'OTHER'
                                  );
                                }
                              }
                            }}
                          />
                        );
                      })()}
                      {form._partyId === '__other__' && (
                        <input
                          className="form-control"
                          style={{ marginTop: '6px' }}
                          placeholder="Enter party name..."
                          value={form.partyName}
                          onChange={e => setField('partyName', e.target.value)}
                          autoFocus
                        />
                      )}
                    </>
                  ) : (
                    <input
                      className="form-control"
                      placeholder="Enter party name (no parties in system yet)"
                      value={form.partyName}
                      onChange={e => setField('partyName', e.target.value)}
                    />
                  )}
                </div>
                <div className="form-group">
                  <label className="form-label">Party Type *</label>
                  <select className="form-control" value={form.partyType} onChange={e => setField('partyType', e.target.value)}>
                    {PARTY_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Vehicle Number</label>
                  <input className="form-control" placeholder="e.g. LHR-1234" value={form.vehicleNumber} onChange={e => setField('vehicleNumber', e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">Container Number</label>
                  <input className="form-control" placeholder="e.g. MSKU1234567" value={form.containerNumber} onChange={e => setField('containerNumber', e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">Driver Name</label>
                  <input className="form-control" placeholder="Driver full name" value={form.driverName} onChange={e => setField('driverName', e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">Driver Contact</label>
                  <input className="form-control" placeholder="e.g. 0300-1234567" value={form.driverContact} onChange={e => setField('driverContact', e.target.value)} />
                </div>
              </div>

              {form.type === 'ENTRY' && (
                <div className="form-grid-2">
                  <div className="form-group">
                    <label className="form-label">Service Charges <span style={{fontWeight:400,color:'var(--color-text-secondary)'}}>(mandi commission, agent fee)</span></label>
                    <input type="number" className="form-control" min="0" step="0.01" placeholder="0.00" value={form.serviceCharges} onChange={e => setField('serviceCharges', e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Receiving Labor <span style={{fontWeight:400,color:'var(--color-text-secondary)'}}>(loading, mandi labor — same field as the batch's Sourcing tab)</span></label>
                    <input type="number" className="form-control" min="0" step="0.01" placeholder="0.00" value={form.laborCost} onChange={e => setField('laborCost', e.target.value)} />
                  </div>
                </div>
              )}

              <div className="form-group">
                <label className="form-label">Notes</label>
                <textarea className="form-control" rows={2} placeholder="Any additional notes" value={form.notes} onChange={e => setField('notes', e.target.value)} />
              </div>

              {/* WASTE type hint */}
              {form.materialType === 'WASTE' && (
                <div style={{ background: 'var(--color-warning-light)', border: '1px solid var(--color-warning-border)', borderRadius: '8px', padding: '10px 14px', marginBottom: '12px', fontSize: '13px', color: 'var(--color-warning-strong)' }}>
                  <strong>Wastage Material:</strong> After saving this gate pass, open the relevant Batch's <strong>Grading</strong> tab and log it there as a Wastage / Loss entry — that's the one place wastage is tracked now, and it'll count against that batch's own numbers.
                </div>
              )}

              {/* Items */}
              <div style={{ marginTop: '4px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                  <strong style={{ fontSize: '14px' }}>Items <span style={{ color: 'var(--color-error)' }}>*</span></strong>
                  {!form.batchId && (
                    <button type="button" className="btn-secondary" style={{ fontSize: '12px', padding: '6px 14px' }} onClick={addItem}>
                      + Add Item
                    </button>
                  )}
                  {form.batchId && (
                    <span style={{ fontSize: 12, color: 'var(--color-accent-hover)', fontStyle: 'italic' }}>
                      One product per batch — to add another product, use a separate gate pass with a different batch
                    </span>
                  )}
                </div>

                {form.items.map((item, i) => (
                  <div key={i} style={{ background: 'var(--color-neutral-50)', border: '1px solid var(--color-border-medium)', borderRadius: '8px', padding: '14px', marginBottom: '10px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px', alignItems: 'center' }}>
                      <span style={{ fontWeight: 600, fontSize: '13px', color: 'var(--color-text-secondary)' }}>Item {i + 1}</span>
                      {form.items.length > 1 && (
                        <button type="button" onClick={() => removeItem(i)} style={{ background: 'none', border: 'none', color: 'var(--color-error)', cursor: 'pointer', fontSize: '12px', fontWeight: 500 }}>
                          ✕ Remove
                        </button>
                      )}
                    </div>
                    <div className="form-grid-2">
                      <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                        <label className="form-label" style={{ color: !item.productId ? 'var(--color-error)' : undefined }}>
                          Product *
                          {item.productId && form.batchId
                            ? <span style={{ marginLeft: 8, color: 'var(--color-accent-hover)', fontSize: 11, fontWeight: 400 }}>Locked to batch — change batch to change product</span>
                            : item.productId
                              ? <span style={{ marginLeft: 8, color: 'var(--color-success)', fontSize: 11, fontWeight: 400 }}>✓ linked to catalog · stock will be tracked</span>
                              : <span style={{ marginLeft: 8, color: 'var(--color-error)', fontSize: 11, fontWeight: 400 }}>required — select batch above to auto-fill</span>}
                        </label>
                        {/* When batch is selected, product is locked — show as read-only badge */}
                        {form.batchId && item.productId ? (
                          <div style={{ padding: '8px 12px', border: '1px solid var(--color-accent-hover)', borderRadius: 6, fontSize: 13, background: 'var(--color-accent-light)', color: 'var(--color-accent-hover)', fontWeight: 500 }}>
                            {item.itemName}{item.itemCode ? ` [${item.itemCode}]` : ''} — product locked from batch
                          </div>
                        ) : (
                          <div style={{ display: 'flex', gap: 8 }}>
                            {purchasableProducts.length === 0 ? (
                              <div style={{ flex: 1, padding: '7px 10px', border: '1px solid var(--color-error)', borderRadius: 6, fontSize: 13, background: 'var(--color-error-light)', color: 'var(--color-error)' }}>
                                No products in catalog yet.
                              </div>
                            ) : (
                              <SearchableSelect
                                required
                                placeholder={form.batchId ? '— Select Batch above to auto-fill product —' : '— Select Product —'}
                                style={{ flex: 1 }}
                                value={item.productId || ''}
                                options={purchasableProducts.map(p => ({
                                  value: p.id,
                                  label: `${p.name}${p.productCode ? ` [${p.productCode}]` : ''}${p.packSize ? ` · ${p.packSize}` : ''}${p.currentStock != null ? ` · Stock: ${p.currentStock} ${p.unit || ''}` : ''}`,
                                }))}
                                onChange={(value) => selectProduct(i, value)}
                              />
                            )}
                            <button type="button" className="btn-secondary" style={{ whiteSpace: 'nowrap', padding: '4px 12px' }} onClick={() => setQuickAddFor(i)}>
                              + New Product
                            </button>
                          </div>
                        )}
                      </div>
                      <div className="form-group">
                        <label className="form-label">Item Name</label>
                        <input className="form-control" readOnly style={{ background: 'var(--color-bg-tertiary)', color: 'var(--color-neutral-700)' }} placeholder="Auto-filled from product" value={item.itemName} />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Item Code</label>
                        <input className="form-control" readOnly style={{ background: 'var(--color-bg-tertiary)' }} placeholder="Auto-filled" value={item.itemCode} />
                      </div>
                      <div className="form-group">
                        <label className="form-label">
                          Quantity (bags / units) *
                          {item.packSizeKg && <span style={{fontSize:'10px',color:'var(--color-success)',marginLeft:6}}>→ fills weight auto</span>}
                        </label>
                        <input type="number" className="form-control" required min="1" step="1" placeholder="e.g. 750" value={item.quantity} onChange={e => setItem(i, 'quantity', e.target.value)} />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Unit *</label>
                        <select className="form-control" value={item.unit} onChange={e => setItem(i, 'unit', e.target.value)}>
                          {UNITS.map(u => <option key={u}>{u}</option>)}
                        </select>
                      </div>
                      <div className="form-group">
                        <label className="form-label">
                          Total Weight (KG)
                          {!item.packSizeKg && <span style={{fontSize:'10px',color:'var(--color-text-secondary)',marginLeft:6}}>— used for KG product stock</span>}
                        </label>
                        <input type="number" className="form-control" min="0" step="any" placeholder="e.g. 30000" value={item.weight} onChange={e => setItem(i, 'weight', e.target.value)} />
                        {item.packSizeKg && item.weight && parseFloat(item.weight) > 0 && (
                          <span style={{fontSize:'11px',color:'var(--color-text-secondary)',marginTop:2,display:'block'}}>
                            ≈ {Math.round(parseFloat(item.weight) / item.packSizeKg).toLocaleString()} bags @ {item.packSizeKg} kg each
                          </span>
                        )}
                        {!item.packSizeKg && item.weight && item.quantity && parseFloat(item.weight) > 0 && parseFloat(item.quantity) > 0 && (
                          <span style={{fontSize:'11px',color:'var(--color-text-secondary)',marginTop:2,display:'block'}}>
                            ≈ {(parseFloat(item.weight) / parseFloat(item.quantity)).toFixed(1)} kg/bag
                          </span>
                        )}
                      </div>
                      <div className="form-group">
                        <label className="form-label">Notes</label>
                        <input className="form-control" placeholder="Optional" value={item.notes} onChange={e => setItem(i, 'notes', e.target.value)} />
                      </div>
                      {form.type === 'ENTRY' && (
                        <>
                          <div className="form-group">
                            <label className="form-label">
                              Bag Type <span style={{fontSize:'10px',color:'var(--color-text-secondary)'}}>— optional, e.g. Katta/Bori this arrived in</span>
                            </label>
                            <select className="form-control" value={item.bagTypeProductId}
                              onChange={e => setItem(i, 'bagTypeProductId', e.target.value)}>
                              <option value="">— None —</option>
                              {byproductProducts.map(p => <option key={p.id} value={p.id}>{p.name}{p.packSize ? ` (${p.packSize})` : ''}</option>)}
                            </select>
                            {byproductProducts.length === 0 && (
                              <span style={{fontSize:11,color:'var(--color-text-secondary)',marginTop:2,display:'block'}}>
                                No bag-type products yet — create one (e.g. "Katta", 10kg Bag) in Product Catalog, category Byproduct.
                              </span>
                            )}
                            {/* Credits Quantity directly, not weight ÷ pack size — Quantity IS the bag
                                count you counted for this shipment; deriving it from weight ÷ the bag
                                product's own nominal pack size instead used to silently disagree with
                                it (500 real bags could total 40,000kg — 80kg/bag average — even if the
                                selected "Katta" product is configured as 25kg, crediting 1,600 instead
                                of the 500 actually received). Caught live 2026-09-08. */}
                            {item.bagTypeProductId && item.quantity && parseFloat(item.quantity) > 0 && (() => {
                              const bagProduct = byproductProducts.find(p => String(p.id) === String(item.bagTypeProductId));
                              const bagCount = Math.floor(parseFloat(item.quantity));
                              // Quantity and Bag Type describe the SAME physical bags — Quantity is how
                              // many, Bag Type's pack size is how much each one holds — so their product
                              // should reasonably match Total Weight. A mismatch (e.g. 750 bags of a
                              // "10kg" bag type but 30,000kg total — implies 40kg/bag, not 10kg) usually
                              // means Quantity, Weight, or the Bag Type picked is wrong. Warn, don't
                              // block — real bags vary a bit, and this data doesn't need to be perfect
                              // to be useful, per the user's own call on this (2026-09-08).
                              const bagPackSizeKg = bagProduct ? parsePackSizeKg(bagProduct.packSize) : null;
                              const weightNum = parseFloat(item.weight);
                              let mismatchWarning = null;
                              if (bagPackSizeKg && weightNum > 0) {
                                const expectedWeight = bagCount * bagPackSizeKg;
                                const diffRatio = Math.abs(expectedWeight - weightNum) / weightNum;
                                if (diffRatio > 0.1) { // >10% off — flag it, don't block
                                  mismatchWarning = `${bagCount.toLocaleString()} bags × ${bagPackSizeKg}kg ≈ ${expectedWeight.toLocaleString()}kg, but Total Weight is ${weightNum.toLocaleString()}kg — double check Quantity, Weight, or Bag Type.`;
                                }
                              }
                              return (
                                <>
                                  <span style={{fontSize:11,color:'var(--color-success)',marginTop:2,display:'block'}}>
                                    ≈ {bagCount.toLocaleString()} {bagProduct?.name || 'bag'} bag{bagCount === 1 ? '' : 's'} will be added to inventory (free)
                                  </span>
                                  {mismatchWarning && (
                                    <span style={{fontSize:11,color:'var(--color-warning)',marginTop:2,display:'block'}}>
                                      ⚠ {mismatchWarning}
                                    </span>
                                  )}
                                </>
                              );
                            })()}
                          </div>
                          <div className="form-group" style={item.unit === 'KG' ? { gridColumn: 'span 2' } : undefined}>
                            <label className="form-label">
                              Price <span style={{fontSize:'10px',color:'var(--color-text-secondary)'}}>— optional</span>
                              {!item.purchasePrice && <span style={{fontSize:11,color:'var(--color-text-secondary)',marginLeft:6,fontWeight:400}}>leave blank if not settled yet — add it later from the gate pass detail view</span>}
                            </label>
                            <div style={{ display: 'flex', gap: 6, alignItems: 'stretch' }}>
                              {/* priceRaw holds exactly what's typed, in whatever unit is currently
                                  selected — kept separate from purchasePrice (always per-KG, the
                                  value actually submitted) so reformatting on toggle/blur never
                                  corrupts an in-progress keystroke (a real bug: re-deriving the
                                  displayed value from purchasePrice on every keystroke made typing
                                  "180" land as "1.0001" — the controlled value was overwriting
                                  itself mid-type). */}
                              <input type="number" className="form-control" min="0" step="any"
                                placeholder={item.priceUnit === 'MANN' ? 'e.g. 7200' : 'e.g. 180'}
                                style={{ flex: 1 }}
                                value={item.priceRaw}
                                onChange={e => {
                                  const typed = e.target.value;
                                  const perKg = typed === '' ? '' : (item.priceUnit === 'MANN' ? parseFloat(typed) / 40 : parseFloat(typed));
                                  setItem(i, 'priceRaw', typed);
                                  setItem(i, 'purchasePrice', typed === '' || isNaN(perKg) ? '' : perKg);
                                }} />
                              {item.unit === 'KG' && (
                                <div style={{ display: 'flex', border: '1px solid var(--color-border-strong)', borderRadius: 6, overflow: 'hidden', flexShrink: 0 }}>
                                  {[['KG', 'per KG'], ['MANN', 'per 40kg']].map(([val, label]) => (
                                    <button key={val} type="button"
                                      onClick={() => {
                                        // One-time conversion at the moment of switching — safe to
                                        // round here since it's not happening mid-keystroke.
                                        if (val !== item.priceUnit) {
                                          const price = parseFloat(item.purchasePrice);
                                          const nextRaw = isNaN(price) ? '' : (val === 'MANN'
                                            ? (price * 40).toFixed(2).replace(/\.?0+$/, '')
                                            : String(price));
                                          setItem(i, 'priceRaw', nextRaw);
                                        }
                                        setItem(i, 'priceUnit', val);
                                      }}
                                      style={{
                                        padding: '0 10px', fontSize: 11, fontWeight: 600, border: 'none', cursor: 'pointer',
                                        background: item.priceUnit === val ? 'var(--color-primary)' : 'var(--color-bg-tertiary)',
                                        color: item.priceUnit === val ? '#fff' : 'var(--color-neutral-700)',
                                      }}>
                                      {label}
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>
                            {item.priceUnit === 'MANN' && item.purchasePrice && (
                              <span style={{fontSize:11,color:'var(--color-text-secondary)',marginTop:2,display:'block'}}>
                                = PKR {parseFloat(item.purchasePrice).toFixed(2)} per KG — same price, just entered per 40kg (mann)
                              </span>
                            )}
                          </div>
                          <div className="form-group">
                            <label className="form-label">Currency</label>
                            <select className="form-control" value={item.purchaseCurrency} onChange={e => setItem(i, 'purchaseCurrency', e.target.value)}>
                              {CURRENCIES.map(c => <option key={c}>{c}</option>)}
                            </select>
                          </div>
                          {item.purchasePrice && (item.quantity || item.weight) && (() => {
                            const useWeight = item.weight && parseFloat(item.weight) > 0;
                            const base = useWeight ? parseFloat(item.weight) : parseFloat(item.quantity);
                            const total = isNaN(base) ? 0 : base * parseFloat(item.purchasePrice);
                            return (
                              <div className="form-group">
                                <label className="form-label">
                                  Line Total
                                  <span style={{fontSize:10,color:'var(--color-text-secondary)',marginLeft:6}}>
                                    {useWeight ? `${Number(item.weight).toLocaleString()} kg × PKR ${item.purchasePrice}/kg` : `${item.quantity} units × PKR ${item.purchasePrice}`}
                                  </span>
                                </label>
                                <input className="form-control" readOnly style={{ background: 'var(--color-bg-tertiary)', fontWeight: 600 }}
                                  value={`${item.purchaseCurrency || 'PKR'} ${total.toLocaleString('en-PK', { maximumFractionDigits: 0 })}`} />
                              </div>
                            );
                          })()}
                          <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                            <label className="form-label">Vendor PO #</label>
                            <input className="form-control" placeholder="Purchase order / reference number" value={item.vendorPo} onChange={e => setItem(i, 'vendorPo', e.target.value)} />
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <div className="modal-footer">
                <button type="button" className="btn-secondary" disabled={formLoading} onClick={() => setShowCreate(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary" disabled={formLoading}>
                  {formLoading ? 'Creating…' : 'Create Gate Pass'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Detail Modal ── */}
      {showDetail && (
        <div className="modal-overlay" onClick={() => setShowDetail(false)}>
          <div className="modal-content" style={{ maxWidth: '700px' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                {selectedPass ? selectedPass.gatePassNumber : 'Gate Pass Details'}
                {selectedPass && <span style={{ marginLeft: '10px' }}>{badge(selectedPass.status, STATUS_STYLE)}</span>}
              </h3>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                {selectedPass && (
                  <PdfActions
                    fetchBlob={() => fetchGatePassPdfBlob(selectedPass)}
                    filename={`${selectedPass.gatePassNumber}.pdf`}
                    label="PDF"
                    className="btn-secondary"
                  />
                )}
                <button className="modal-close" onClick={() => setShowDetail(false)}>✕</button>
              </div>
            </div>

            {detailLoading ? (
              <div className="loading-state">Loading details…</div>
            ) : !selectedPass ? (
              <InlineAlert msg="Failed to load gate pass details" />
            ) : (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 20px', marginBottom: '18px' }}>
                  {[
                    ['Type',          badge(selectedPass.type, TYPE_STYLE)],
                    ['Material',      fmt(selectedPass.materialType)],
                    ['Date',          selectedPass.gatePassDate],
                    ['Warehouse',     selectedPass.warehouseName || '—'],
                    ['Party Name',    selectedPass.partyName],
                    ['Party Type',    selectedPass.partyType],
                    ['Vehicle #',     selectedPass.vehicleNumber || '—'],
                    ['Container #',   selectedPass.containerNumber || '—'],
                    ['Driver Name',   selectedPass.driverName || '—'],
                    ['Driver Contact', selectedPass.driverContact || '—'],
                    ['Total Qty',     selectedPass.totalQuantity ?? '—'],
                    ['Total Weight',  selectedPass.totalWeight ? `${selectedPass.totalWeight} KG` : '—'],
                    ['Linked Purchase', selectedPass.linkedPurchaseNumber
                      ? <span style={{ color: 'var(--color-success)', fontWeight: 600 }}>{selectedPass.linkedPurchaseNumber}</span>
                      : (selectedPass.type === 'ENTRY' ? <span style={{ color: 'var(--color-warning)' }}>Not yet created</span> : '—')],
                    ['Created By',    selectedPass.createdByName || '—'],
                    ['Issued By',     selectedPass.issuedByName  || '—'],
                    ['Issued At',     selectedPass.issuedAt ? new Date(selectedPass.issuedAt).toLocaleString() : '—'],
                    ['Created At',    selectedPass.createdAt ? new Date(selectedPass.createdAt).toLocaleString() : '—'],
                  ].map(([label, value]) => (
                    <div key={label}>
                      <div style={{ fontSize: '11px', color: 'var(--color-text-tertiary)', marginBottom: '2px' }}>{label}</div>
                      <div style={{ fontSize: '13px', fontWeight: 500 }}>{value}</div>
                    </div>
                  ))}
                </div>

                {selectedPass.notes && (
                  <div style={{ background: 'var(--color-neutral-50)', border: '1px solid var(--color-border-medium)', borderRadius: '8px', padding: '10px 14px', marginBottom: '14px', fontSize: '13px' }}>
                    <strong>Notes:</strong> {selectedPass.notes}
                  </div>
                )}

                {selectedPass.cancelReason && (
                  <div style={{ background: 'var(--color-error-light)', border: '1px solid var(--color-error-border)', borderRadius: '8px', padding: '10px 14px', marginBottom: '14px', fontSize: '13px', color: 'var(--color-error)' }}>
                    <strong>Cancel Reason:</strong> {selectedPass.cancelReason}
                  </div>
                )}

                <h4 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '10px', color: 'var(--color-neutral-700)' }}>
                  Items ({selectedPass.items?.length || 0})
                </h4>
                {selectedPass.items?.length > 0 ? (
                  <div className="table-container">
                    <table className="data-table" style={{ fontSize: '12px' }}>
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Item Name</th>
                          <th>Code</th>
                          <th>Qty</th>
                          <th>Unit</th>
                          <th>Weight (KG)</th>
                          {selectedPass.type === 'ENTRY' && <><th>Purchase Price</th><th>Curr.</th><th>Vendor PO</th></>}
                          <th>Notes</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedPass.items.map((item, i) => (
                          <tr key={item.id}>
                            <td>{i + 1}</td>
                            <td style={{ fontWeight: 500 }}>{item.itemName}</td>
                            <td>{item.itemCode || '—'}</td>
                            <td>{item.quantity}</td>
                            <td>{item.unit}</td>
                            <td>{item.weight || '—'}</td>
                            {selectedPass.type === 'ENTRY' && (
                              <>
                                <td>
                                  {editingPriceItemId === item.id ? (
                                    <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                                      <input
                                        type="number" min="0" step="0.01" autoFocus
                                        style={{ width: 90, padding: '3px 6px', fontSize: 12, border: '1px solid var(--color-primary)', borderRadius: 4 }}
                                        value={editingPriceValue}
                                        onChange={e => setEditingPriceValue(e.target.value)}
                                      />
                                      <button type="button" disabled={savingPrice} onClick={() => saveEditPrice(item)}
                                        style={{ background: 'var(--color-success)', color: '#fff', border: 'none', borderRadius: 4, padding: '3px 7px', cursor: 'pointer', fontSize: 11 }}>✓</button>
                                      <button type="button" disabled={savingPrice} onClick={() => setEditingPriceItemId(null)}
                                        style={{ background: 'var(--color-bg-tertiary)', border: 'none', borderRadius: 4, padding: '3px 7px', cursor: 'pointer', fontSize: 11 }}>✗</button>
                                    </div>
                                  ) : (
                                    <span style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                                      {item.purchasePrice ? Number(item.purchasePrice).toLocaleString() : <em style={{ color: 'var(--color-text-tertiary)' }}>not set</em>}
                                      <button type="button" onClick={() => startEditPrice(item)}
                                        style={{ background: 'none', border: '1px solid var(--color-border-strong)', borderRadius: 4, padding: '1px 6px', cursor: 'pointer', fontSize: 10, color: 'var(--color-text-secondary)' }}>
                                        {item.purchasePrice ? 'edit' : '+ set price'}
                                      </button>
                                    </span>
                                  )}
                                </td>
                                <td>{item.purchaseCurrency || '—'}</td>
                                <td>{item.vendorPo || '—'}</td>
                              </>
                            )}
                            <td>{item.notes || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p style={{ color: 'var(--color-text-tertiary)', fontSize: '13px' }}>No items recorded.</p>
                )}

                {/* Attachments (weighbridge slips, photos, etc.) */}
                <div style={{ marginTop: '20px', borderTop: '1px solid var(--color-border-medium)', paddingTop: '16px' }}>
                  <h4 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '10px', color: 'var(--color-neutral-700)' }}>
                    Attachments <span style={{ fontWeight: 400, fontSize: '12px', color: 'var(--color-text-secondary)' }}>(weighbridge slips, photos, etc.)</span>
                  </h4>
                  <AttachmentPanel entityType="GATE_PASS" entityId={selectedPass.id} />
                </div>
              </>
            )}
          </div>
        </div>
      )}
      {/* ── Quick Add Product Modal (from within an item row) ── */}
      {quickAddFor !== null && (
        <ProductForm
          vendors={partyList.filter(p => p.customerType === 'VENDOR' || p.customerType === 'BOTH')}
          excludeCategories={['CONSUMABLE']}
          onClose={() => setQuickAddFor(null)}
          onSave={handleProductCreated(quickAddFor)}
        />
      )}
      {/* ── Toast Notification ── */}
      {toast && (
        <div style={{
          position: 'fixed', bottom: '24px', right: '24px', zIndex: 9999,
          background: toast.type === 'error' ? 'var(--color-error-light)' : 'var(--color-success-light)',
          color: toast.type === 'error' ? 'var(--color-error)' : 'var(--color-success)',
          border: `1px solid ${toast.type === 'error' ? 'var(--color-error-border)' : 'var(--color-success-border)'}`,
          padding: '14px 22px', borderRadius: '10px', fontSize: '14px', fontWeight: 600,
          boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
          display: 'flex', alignItems: 'center', gap: '10px',
        }}>
          {toast.msg}
        </div>
      )}
    </div>
  );
};

export default GatePassManagement;
